import { useState, useRef, useCallback } from 'react';
import axios from 'axios';

// Ensure API_BASE always includes /api/v1
const rawApiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';
const API_BASE = rawApiUrl.endsWith('/api/v1') ? rawApiUrl : `${rawApiUrl}/api/v1`;

export type JobStatus =
  | 'queued'
  | 'processing'
  | 'generating_content'
  | 'generating_images'
  | 'uploading_images'
  | 'publishing'
  | 'completed'
  | 'failed';

export interface JobResponse {
  job_id: string;
  status: JobStatus;
  progress_percent: number;
  current_step: string;
  result_url?: string;
  content_preview?: string;
  content_id?: string;
  error_message?: string;
  created_at?: string;
  updated_at?: string;
  // Full result data (populated when job completes)
  result?: {
    html_content?: string;
    title?: string;
    meta_description?: string;
    word_count?: number;
    image_count?: number;
    tags?: string[];
    images?: Array<{ url: string; alt: string }>;
    s3_key?: string;
    s3_url?: string;
  };
}

export interface GenerationConfig {
  topic: string;
  target_word_count?: number;
  target_country?: string;
  generate_images?: boolean;
  image_count?: number;
  tone?: string;
  keywords?: string[];
}

interface UseJobPollerReturn {
  startJob: (config: GenerationConfig, authHeaders: Record<string, string>) => Promise<void>;
  jobState: JobResponse | null;
  isPolling: boolean;
  error: string | null;
  stopPolling: () => void;
  resetState: () => void;
}

export const useJobPoller = (): UseJobPollerReturn => {
  const [jobState, setJobState] = useState<JobResponse | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ref to store the interval ID so we can clear it
  const pollInterval = useRef<number | null>(null);
  const authHeadersRef = useRef<Record<string, string>>({});

  const stopPolling = useCallback(() => {
    if (pollInterval.current) {
      clearInterval(pollInterval.current);
      pollInterval.current = null;
    }
    setIsPolling(false);
  }, []);

  const resetState = useCallback(() => {
    stopPolling();
    setJobState(null);
    setError(null);
  }, [stopPolling]);

  const startJob = async (config: GenerationConfig, authHeaders: Record<string, string>) => {
    // Reset any previous state
    resetState();
    setIsPolling(true);
    setError(null);
    authHeadersRef.current = authHeaders;

    try {
      // 1. Kick off the job
      const { data } = await axios.post(`${API_BASE}/blog/generate`, config, {
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        }
      });

      const jobId = data.job_id;
      setJobState({
        job_id: jobId,
        status: 'queued',
        progress_percent: 0,
        current_step: 'Initializing...'
      });

      // 2. Start Polling
      let consecutiveErrors = 0;
      const MAX_CONSECUTIVE_ERRORS = 5;
      const MAX_POLL_ATTEMPTS = 60; // 60 * 2s = 2 minutes max
      let pollAttempts = 0;

      pollInterval.current = window.setInterval(async () => {
        pollAttempts++;

        // Timeout after max attempts to prevent infinite polling
        if (pollAttempts >= MAX_POLL_ATTEMPTS) {
          stopPolling();
          setError('Generation is taking longer than expected. Check History for results.');
          return;
        }

        try {
          const statusRes = await axios.get(`${API_BASE}/jobs/${jobId}`, {
            headers: authHeadersRef.current
          });

          const jobData = statusRes.data;
          setJobState(jobData);
          consecutiveErrors = 0; // Reset on success

          if (jobData.status === 'completed' || jobData.status === 'failed') {
            stopPolling();
            if (jobData.status === 'failed') {
              setError(jobData.error_message || 'Generation failed');
            }
          }
        } catch (err) {
          consecutiveErrors++;
          console.error("Polling error", err);

          // Only stop on persistent errors
          if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
            stopPolling();
            setError('Lost connection to server. Please try again.');
          }
        }
      }, 2000); // Poll every 2 seconds

    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string } } };
      setError(axiosError.response?.data?.detail || "Failed to start generation");
      setIsPolling(false);
    }
  };

  return { startJob, jobState, isPolling, error, stopPolling, resetState };
};

export default useJobPoller;
